import type { ReactElement } from 'react';
import type { MediaFormatUsageDto, MediaStorageDto } from '@gr/core/content/media/recompress';
import { Link, useT } from '../../../../app/di';
import { formatFileSize, formatNumber } from '../../../../shared/format';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Button } from '../../../../ui-kit/Button/Button';
import { Card } from '../../../../ui-kit/Card/Card';
import { DataTable } from '../../../../ui-kit/DataTable/DataTable';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';

export interface MediaStorageReportProps {
  storage: MediaStorageDto;
  /** TASK-UC-1.CC-1: в блоге уже идёт фоновая задача — её отчёт (TASK-UC-3); null — не идёт. */
  busyTask: { blogId: string; taskId: string } | null;
  onRecompress(): void;
}

/** MED-UC-8, MED-UC-9.CC-6: объём медиа блога всего и по форматам, вход в пережатие. */
export function MediaStorageReport({ storage, busyTask, onRecompress }: MediaStorageReportProps): ReactElement {
  const { t, tp, locale } = useT();
  const size = (bytes: number): string => formatFileSize(bytes, locale);
  if (storage.count === 0) return <EmptyState title={t('media.storage.empty')} text={t('media.storage.emptyText')} />;
  return (
    <div className="u-stack-lg">
      <Card title={t('media.storage.total', { size: size(storage.totalBytes) })}>
        <div className="u-stack">
          <p className="u-muted u-small">{t('media.storage.intro')}</p>
          <p>{tp('media.storage.files', storage.count, { n: formatNumber(storage.count, locale) })}</p>
          {storage.unmeasured > 0 ? <Alert tone="info">{tp('media.storage.unmeasured', storage.unmeasured, { n: storage.unmeasured })}</Alert> : null}
          {storage.recompress.available ? (
            <div className="u-stack">
              <p className="u-muted u-small">{t('media.storage.recompressHint')}</p>
              {busyTask ? (
                <Alert tone="info">
                  {t('media.storage.busy')}{' '}
                  <Link to="task" params={{ blogId: busyTask.blogId, taskId: busyTask.taskId }}>
                    {t('media.storage.busyLink')}
                  </Link>
                </Alert>
              ) : null}
              <div>
                <Button variant="primary" icon="layers" disabled={busyTask !== null} onClick={onRecompress}>
                  {t('media.storage.recompress')}
                </Button>
              </div>
            </div>
          ) : (
            <Alert tone="warning">{t('media.storage.unavailable')}</Alert>
          )}
        </div>
      </Card>
      <Card title={t('media.storage.byFormat')}>
        <DataTable<MediaFormatUsageDto>
          label={t('media.storage.byFormat')}
          rows={storage.formats}
          rowKey={(row) => `${row.kind}:${row.ext}`}
          columns={[
            { key: 'ext', header: t('media.storage.format'), primary: true, cell: (row) => row.ext.toUpperCase() },
            { key: 'count', header: t('media.storage.count'), align: 'end', cell: (row) => formatNumber(row.count, locale) },
            { key: 'bytes', header: t('media.storage.bytes'), align: 'end', cell: (row) => size(row.bytes) },
          ]}
        />
      </Card>
    </div>
  );
}
